import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import { BRAND_NAME } from '../../../../shared/env';
import appStoreImage from '../../assets/hscan-landing/app_store.png';
import googlePlayImage from '../../assets/hscan-landing/google_play.png';
import hpacsSolutionImage from '../../assets/hscan-landing/hpacs_solution_new.png';
import chartIcon from '../../assets/icons/icon_chart.png';
import downloadIcon from '../../assets/icons/icon_download.png';
import uploadIcon from '../../assets/icons/icon_upload.png';
import { BlueRoundedButtonWithIcon } from '../../common/components/BlueRoundedButton';
import { ColoredIcon } from '../../common/components/Icon';
import { Spacer } from '../../common/components/Spacer';
import { isMobile as detectMobile } from '../../utils';

import { LinkCodeForm } from './LinkCodeForm';

const SECTION1_CARDS_MOBILE_HORIZONTAL_PADDING = 27;
const SECTION1_CARDS_MOBILE_THRESHOLD = 1130; // shrink 여부
const SECTION1_CARDS_MAX_WIDTH =
  SECTION1_CARDS_MOBILE_THRESHOLD -
  SECTION1_CARDS_MOBILE_HORIZONTAL_PADDING * 2;
const SECTION1_CARD_WIDTH = 263;
const SECTION1_MOBILE_CARD_WIDTH = 360;
const SECTION1_CARDS_NARROW_THRESHOLD = 650; // mobile 여부
const SECTION1_NARROW_WIDTH =
  SECTION1_CARDS_NARROW_THRESHOLD -
  SECTION1_CARDS_MOBILE_HORIZONTAL_PADDING * 2;

const FirstSection = styled.section`
  padding-top: 76px;
  padding-bottom: 20px;
  background-color: ${p => p.theme.colors.white};

  @media (max-width: ${SECTION1_CARDS_MOBILE_THRESHOLD - 1}px) {
    background-color: unset;
  }
  @media (max-width: ${SECTION1_CARDS_NARROW_THRESHOLD - 1}px) {
    padding-top: 24px;
  }
`;

const Section1TextWrapper = styled.div`
  background-image: url(${hpacsSolutionImage});
  background-size: contain;
  background-position: center right;
  background-repeat: no-repeat;

  @container (width < ${SECTION1_NARROW_WIDTH}px) {
    display: block;
    margin: 0 auto;
    max-width: 360px;
  }

  @supports not (container-type: inline-size) {
    @media (max-width: ${SECTION1_CARDS_NARROW_THRESHOLD - 1}px) {
      display: block;
      margin: 0 auto;
      max-width: 360px;
    }
  }
`;
const Section1Heading = styled.h2`
  display: flex;
  align-items: center;
  margin: 0;
  margin-bottom: 24px;
  min-height: 102px;
  color: var(--HScan_blue_grey, #00025a);
  word-break: keep-all;
`;
const Section1EmphasizedTextWrapper = styled.div`
  font-size: 20px;
  font-weight: 900;
`;
const Section1EmphasizedText = styled.span`
  color: var(--HScan_Light_Blue, #2e5be0);
`;

const Cards = styled.div`
  container-type: inline-size;
  overflow: auto;

  // 1x4
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 24px;
  max-width: ${SECTION1_CARDS_MAX_WIDTH}px;
  margin: 0 auto;

  // 2x2
  @container (width < ${SECTION1_CARDS_MAX_WIDTH}px) {
    display: grid;
    grid-template-columns: 50% 50%;
    justify-content: center;
    max-width: ${SECTION1_MOBILE_CARD_WIDTH * 2}px;
    width: 100%;
    border: 1px solid var(--Hscan_light_grey, #e7e7e7);
    border-radius: 10px;
    gap: 0;
    background-color: ${p => p.theme.colors.white};
  }

  // 4x1
  @container (width < ${SECTION1_NARROW_WIDTH}px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: ${SECTION1_MOBILE_CARD_WIDTH}px;
  }

  @supports not (container-type: inline-size) {
    @media (max-width: ${SECTION1_CARDS_MOBILE_THRESHOLD - 1}px) {
      display: grid;
      grid-template-columns: 50% 50%;
      justify-content: center;
      max-width: ${SECTION1_MOBILE_CARD_WIDTH * 2}px;
      width: 100%;
      border: 1px solid var(--Hscan_light_grey, #e7e7e7);
      border-radius: 10px;
      gap: 0;
      background-color: ${p => p.theme.colors.white};
    }
    @media (max-width: ${SECTION1_CARDS_NARROW_THRESHOLD - 1}px) {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      max-width: ${SECTION1_MOBILE_CARD_WIDTH}px;
    }
  }
`;
const Card = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  flex: 1;
  max-width: ${SECTION1_CARD_WIDTH}px;
  box-sizing: border-box;
  padding-top: 44px;
  padding-right: 28px;
  padding-left: 30px;
  padding-bottom: 44px;
  border: 1px solid var(--Hscan_light_grey, #e7e7e7);
  border-radius: 10px;
  word-break: keep-all;

  // 2x2
  @container (width < ${SECTION1_CARDS_MAX_WIDTH}px) {
    max-width: ${SECTION1_MOBILE_CARD_WIDTH}px;
    padding-top: 30px;
    padding-right: 40px;
    padding-bottom: 38px;
    padding-left: 35px;
    border: none;
    border-top: 1px dashed #e5e5e5;
    &:nth-child(-n + 2) {
      border: none;
    }
    &:nth-child(2n) {
      border-left: 1px dashed #e5e5e5;
    }
    &:last-child {
      max-width: none;
      grid-column: span 2;
    }
    border-radius: 0px;
  }

  // 4x1
  @container (width < ${SECTION1_NARROW_WIDTH}px) {
    &:nth-child(2n) {
      border-left: none;
    }
    &:nth-child(n + 1) {
      border-top: 1px dashed #e5e5e5;
    }
    &:first-child {
      border: none;
    }
  }

  @supports not (container-type: inline-size) {
    @media (max-width: ${SECTION1_CARDS_MOBILE_THRESHOLD - 1}px) {
      max-width: ${SECTION1_MOBILE_CARD_WIDTH}px;
      padding-top: 30px;
      padding-right: 40px;
      padding-bottom: 38px;
      padding-left: 35px;
      border: none;
      border-top: 1px dashed #e5e5e5;
      &:nth-child(-n + 2) {
        border: none;
      }
      &:nth-child(2n) {
        border-left: 1px dashed #e5e5e5;
      }
      border-radius: 0px;
    }

    @media (max-width: ${SECTION1_CARDS_NARROW_THRESHOLD - 1}px) {
      &:nth-child(2n) {
        border-left: none;
      }
      &:nth-child(n + 1) {
        border-top: 1px dashed #e5e5e5;
      }
      &:first-child {
        border: none;
      }
    }
  }
`;
const CardHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  @container (width < ${SECTION1_CARDS_MAX_WIDTH}px) {
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }

  @supports not (container-type: inline-size) {
    @media (max-width: ${SECTION1_CARDS_MOBILE_THRESHOLD - 1}px) {
      flex-direction: row;
      align-items: center;
      justify-content: center;
    }
  }
`;
const CardIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 96px;
  border-radius: 96px;
  background: var(--HPACS_BG_grey, #f5f5f5);

  @container (width < ${SECTION1_CARDS_MAX_WIDTH}px) {
    width: 46px;
    height: 46px;
  }

  @supports not (container-type: inline-size) {
    @media (max-width: ${SECTION1_CARDS_MOBILE_THRESHOLD - 1}px) {
      width: 46px;
      height: 46px;
    }
  }
`;
const CardHeading = styled.h3`
  margin: 0;
  margin-top: 40px;
  align-self: flex-start;
  font-size: 14px;
  line-height: 1.5;
  font-weight: 700;
  color: var(--HScan_blue_grey, #00025a);
  white-space: normal;
  word-break: keep-all;
  text-align: center;

  .code-form & {
    margin-top: 0px;
  }

  @container (width < ${SECTION1_CARDS_MAX_WIDTH}px) {
    margin: 0;
    margin-left: 11px;
    align-self: auto;
    line-height: normal;
    white-space: normal;
  }

  @supports not (container-type: inline-size) {
    @media (max-width: ${SECTION1_CARDS_MOBILE_THRESHOLD - 1}px) {
      margin: 0;
      margin-left: 11px;
      align-self: auto;
      line-height: normal;
      white-space: normal;
    }
  }
`;
const CardText = styled.p<{ $fontSize?: number }>`
  margin: 0;
  margin-top: 40px;
  font-size: ${p => p.$fontSize ?? 16}px;
  line-height: 1.8em;
  color: var(--HScan_dark_blue, #16172c);
`;
const CardText1 = styled(CardText)`
  margin-top: 38px;
`;
const CardText2 = styled(CardText)`
  margin-top: 47px;
`;

const CardContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FirstSectionPhraseWrapper = styled.div`
  padding: 80px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const FirstSectionPhraseHeading = styled.div`
  font-size: 39px;
  color: var(--HScan_blue_grey, #00025a);
  word-break: keep-all;

  @media (max-width: ${SECTION1_CARDS_MOBILE_THRESHOLD - 1}px) {
    font-size: 35px;
  }
`;
const FirstSectionPhraseText = styled.div`
  margin-top: 40px;
  max-width: 623px;
  font-size: 16px;
  line-height: 30px;
  text-align: center;
  color: var(--HScan_dark_blue, #16172c);
  word-break: keep-all;
`;

const AppDownloadSection = styled.section`
  background: var(--HScan_blue_grey, #00025a);
  color: white;
  padding: 70px 0;
`;

const AppDownloadSectionPhraseWrapper = styled.div``;
const AppDownloadSectionPhraseHeading = styled.div`
  font-size: 39px;
  text-align: center;
`;
const AppDownloadSectionPhraseText = styled.div`
  margin-top: 20px;
`;

const AppDownloadButtons = styled.div`
  margin-top: 40px;
  line-height: 0;
`;
const AppDownloadButton = styled.img`
  height: 88px;
`;

const SectionContent = styled.div`
  margin: 0 auto; /* center */
  width: 100%;
  box-sizing: border-box;
  padding-left: ${SECTION1_CARDS_MOBILE_HORIZONTAL_PADDING}px;
  padding-right: ${SECTION1_CARDS_MOBILE_HORIZONTAL_PADDING}px;
  container-type: inline-size;

  ${AppDownloadSection} & {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

export const Landing = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.landing',
  });
  const navigate = useNavigate();

  const [pageWidth, setPageWidth] = useState(document.body.clientWidth);
  useEffect(() => {
    const updatePageWidth = () => {
      setPageWidth(document.body.clientWidth);
    };
    window.addEventListener('resize', updatePageWidth);
    return () => {
      window.removeEventListener('resize', updatePageWidth);
    };
  });

  const isCardsMobile = pageWidth < SECTION1_CARDS_MOBILE_THRESHOLD;
  const isCardsNarrow = pageWidth < SECTION1_CARDS_NARROW_THRESHOLD;
  const cardIconSize = isCardsMobile ? '28px' : '60px';

  const isMobileDevice = detectMobile();

  return (
    <>
      <FirstSection>
        <SectionContent>
          {isCardsNarrow && (
            <Section1TextWrapper>
              <Section1Heading>
                <Section1EmphasizedTextWrapper>
                  <Trans
                    t={t}
                    i18nKey="firstSection.phrase"
                    components={[<Section1EmphasizedText />, <br />]}
                  />
                </Section1EmphasizedTextWrapper>
              </Section1Heading>
            </Section1TextWrapper>
          )}
          <Cards>
            <Card>
              <CardContentWrapper>
                <CardHeader>
                  <CardIconWrapper>
                    <ColoredIcon
                      imageUrl={chartIcon}
                      width={cardIconSize}
                      color="#1862fe"
                    />
                  </CardIconWrapper>
                  <CardHeading>{t('cards.1.heading')}</CardHeading>
                </CardHeader>
                <CardText>{t('cards.1.text')}</CardText>
              </CardContentWrapper>
              <CardContentWrapper>
                <BlueRoundedButtonWithIcon
                  maxWidth="194px"
                  marginTop="50px"
                  onClick={() => navigate('/images-list')}>
                  {t('cards.1.button')}
                </BlueRoundedButtonWithIcon>
              </CardContentWrapper>
            </Card>
            <Card>
              <CardContentWrapper>
                <CardHeader>
                  <CardIconWrapper>
                    <ColoredIcon
                      imageUrl={uploadIcon}
                      width={cardIconSize}
                      color="#1862fe"
                    />
                  </CardIconWrapper>
                  <CardHeading>{t('cards.2.heading')}</CardHeading>
                </CardHeader>
                <CardText>
                  <Trans t={t} i18nKey="cards.2.text" components={[<wbr />]} />
                </CardText>
              </CardContentWrapper>
              <CardContentWrapper>
                <BlueRoundedButtonWithIcon
                  maxWidth="194px"
                  marginTop="50px"
                  onClick={() => navigate('/send-images')}>
                  {t('cards.2.button')}
                </BlueRoundedButtonWithIcon>
              </CardContentWrapper>
            </Card>
            {!isMobileDevice && (
              <Card className="code-form">
                <CardHeader>
                  <CardHeading>{t('cards.3.heading')}</CardHeading>
                </CardHeader>
                <CardText1>
                  {isCardsMobile ? (
                    t('cards.3.text1mobile')
                  ) : (
                    <Trans
                      t={t}
                      i18nKey="cards.3.text1"
                      components={[<br />, <wbr />]}
                    />
                  )}
                </CardText1>
                <Spacer height={38} />
                <LinkCodeForm />
                <CardText2 $fontSize={12}>
                  {isCardsMobile
                    ? t('cards.3.text2mobile', { brandName: BRAND_NAME })
                    : t('cards.3.text2', { brandName: BRAND_NAME })}
                </CardText2>
              </Card>
            )}
          </Cards>
          {!isCardsNarrow && (
            <FirstSectionPhraseWrapper>
              <FirstSectionPhraseHeading>
                {t('firstSection.heading')}
              </FirstSectionPhraseHeading>
              <FirstSectionPhraseText>
                {t('firstSection.text')}
              </FirstSectionPhraseText>
            </FirstSectionPhraseWrapper>
          )}
        </SectionContent>
      </FirstSection>
      {/* <AppDownloadSection>
        <SectionContent>
          <AppDownloadSectionPhraseWrapper>
            <AppDownloadSectionPhraseHeading>
              {t('appDownloadSection.heading')}
            </AppDownloadSectionPhraseHeading>
            <AppDownloadSectionPhraseText>
              {t('appDownloadSection.text')}
            </AppDownloadSectionPhraseText>
          </AppDownloadSectionPhraseWrapper>
          <AppDownloadButtons>
            <AppDownloadButton src={googlePlayImage} />
            <Spacer width={90} />
            <AppDownloadButton src={appStoreImage} />
          </AppDownloadButtons>
        </SectionContent>
      </AppDownloadSection> */}
    </>
  );
};
